/* R51 PDP science motion: five product-specific decorative loops drawn on a transparent layer over existing artwork.
   nad (NMN): energy sparks spiral into the cell. sirt1 (Resveratrol): a slow protective ring deflects specks.
   autophagy (Spermidine): debris spirals in and is recycled. senescent (Fisetin): dim cells fade away, fresh ones appear.
   follicle (Hair): fine strands rise and sway. Decorative only: no data, no clinical simulation.
   Autoplays while visible, stops offscreen and in a hidden tab. Reduced motion draws nothing, so the original image stays the whole picture. */
(() => {
  if (customElements.get('masi-pdp-r51-art')) return;
  const TAU = Math.PI * 2, FPS = 30;
  const LOOP = { nad: 12, sirt1: 16, autophagy: 14, senescent: 18, follicle: 12 };
  const frac = v => v - Math.floor(v);
  const rng = seed => () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };
  const rgb = hex => { const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim()); const n = parseInt(m ? m[1] : '71ebff', 16); return [n >> 16 & 255, n >> 8 & 255, n & 255]; };
  const col = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
  const glow = (ctx, x, y, r, c, a) => { const g = ctx.createRadialGradient(x, y, 0, x, y, r); g.addColorStop(0, col(c, a)); g.addColorStop(1, col(c, 0)); ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, TAU); ctx.fill(); };

  const ART = {
    nad(ctx, w, h, t, env) {
      const P = LOOP.nad, cx = w * env.cx, cy = h * env.cy, R = Math.min(w, h) * env.r;
      for (const s of env.items) {
        const p = frac(s.o + t / P);
        const rad = R * (2.3 - 1.35 * p), a = s.a + p * TAU * .9;
        const x = cx + rad * Math.cos(a), y = cy + rad * Math.sin(a) * .92;
        const alpha = Math.sin(Math.PI * p) * .85;
        glow(ctx, x, y, s.size * 5, env.light, alpha * .55);
        ctx.fillStyle = col(env.white, alpha); ctx.beginPath(); ctx.arc(x, y, s.size, 0, TAU); ctx.fill();
      }
      const pulse = .5 + .5 * Math.sin(TAU * t / P * 3);
      ctx.strokeStyle = col(env.light, .18 + .18 * pulse); ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.arc(cx, cy, R * (1.02 + .03 * pulse), 0, TAU); ctx.stroke();
    },
    sirt1(ctx, w, h, t, env) {
      const P = LOOP.sirt1, cx = w * env.cx, cy = h * env.cy, R = Math.min(w, h) * env.r, ring = R * 1.22;
      ctx.save(); ctx.translate(cx, cy); ctx.rotate(TAU * t / P);
      ctx.setLineDash([R * .09, R * .06]); ctx.strokeStyle = col(env.light, .55); ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(0, 0, ring, 0, TAU); ctx.stroke(); ctx.restore(); ctx.setLineDash([]);
      glow(ctx, cx, cy, ring * 1.15, env.light, .07);
      for (const s of env.items) {
        const p = frac(s.o + t / P * 2), u = p < .5 ? p * 2 : 2 - p * 2;
        const rad = ring * (1.9 - .85 * u), x = cx + rad * Math.cos(s.a), y = cy + rad * Math.sin(s.a);
        const alpha = Math.sin(Math.PI * p) * .7;
        ctx.fillStyle = col(env.speck, alpha); ctx.beginPath(); ctx.arc(x, y, s.size, 0, TAU); ctx.fill();
        if (u > .92) glow(ctx, cx + ring * Math.cos(s.a), cy + ring * Math.sin(s.a), R * .12, env.light, .35 * (u - .92) / .08);
      }
    },
    autophagy(ctx, w, h, t, env) {
      const P = LOOP.autophagy, cx = w * env.cx, cy = h * env.cy, R = Math.min(w, h) * env.r;
      for (const s of env.items) {
        const p = frac(s.o + t / P), rad = R * (2.1 - 1.9 * p), a = s.a + p * TAU * 1.4;
        const x = cx + rad * Math.cos(a), y = cy + rad * Math.sin(a), size = s.size * (1.4 - p);
        const alpha = Math.min(1, Math.sin(Math.PI * p) * 1.4) * (p > .8 ? (1 - p) / .2 : 1);
        ctx.fillStyle = col(p > .55 ? env.light : env.speck, alpha * .8);
        ctx.beginPath(); ctx.ellipse(x, y, size * 1.4, size, a, 0, TAU); ctx.fill();
      }
      const pulse = .5 + .5 * Math.sin(TAU * t / P * 2);
      glow(ctx, cx, cy, R * .55, env.light, .1 + .12 * pulse);
    },
    senescent(ctx, w, h, t, env) {
      const P = LOOP.senescent, cx = w * env.cx, cy = h * env.cy, R = Math.min(w, h) * env.r;
      for (const s of env.items) {
        const p = frac(s.o + t / P), x = cx + R * s.d * Math.cos(s.a + TAU * t / P), y = cy + R * s.d * Math.sin(s.a + TAU * t / P) * .9;
        const old = p < .5, k = old ? p / .5 : (p - .5) / .5, fade = Math.sin(Math.PI * k);
        const size = R * (old ? .11 : .08) * (old ? 1 - .35 * k : .6 + .4 * k);
        ctx.fillStyle = old ? col([150, 160, 168], .45 * fade) : col(env.light, .5 * fade);
        ctx.strokeStyle = old ? col([120, 130, 140], .5 * fade) : col(env.white, .6 * fade);
        ctx.lineWidth = 1; ctx.beginPath(); ctx.arc(x, y, size, 0, TAU); ctx.fill(); ctx.stroke();
      }
    },
    follicle(ctx, w, h, t, env) {
      const P = LOOP.follicle, cx = w * env.cx, cy = h * env.cy, R = Math.min(w, h) * env.r;
      ctx.lineCap = 'round';
      for (const s of env.items) {
        const p = frac(s.o + t / P), len = R * (1.1 + .9 * s.d) * Math.min(1, p * 1.6), alpha = Math.sin(Math.PI * p) * .75;
        const x0 = cx + R * .75 * s.x, y0 = cy + R * .55, sway = Math.sin(TAU * (t / P * 2) + s.a) * R * .12;
        ctx.strokeStyle = col(env.light, alpha); ctx.lineWidth = 1.6;
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.quadraticCurveTo(x0 + sway * .5, y0 - len * .5, x0 + sway, y0 - len); ctx.stroke();
        glow(ctx, x0 + sway, y0 - len, 5, env.white, alpha * .6);
      }
    }
  };

  customElements.define('masi-pdp-r51-art', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.draw = ART[this.dataset.art];
      this.canvas = this.querySelector('canvas');
      this.ctx = this.canvas?.getContext('2d');
      if (!this.draw || !this.ctx) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const r = rng([...this.dataset.art].reduce((a, c) => a + c.charCodeAt(0), 7));
      const card = this.dataset.mode === 'card';
      const accent = rgb(getComputedStyle(this).getPropertyValue('--r51-accent'));
      // Card mode sits over the dark benefit card: light glows. Stage mode sits on a pale panel: accent and navy.
      this.env = {
        cx: .5, cy: card ? .5 : .47, r: card ? .19 : .24,
        light: accent, white: card ? [255, 255, 255] : [11, 31, 58], speck: card ? [255, 255, 255] : [11, 31, 58],
        items: Array.from({ length: 26 }, () => ({ o: r(), a: r() * TAU, size: 1.2 + r() * 2.2, d: .4 + r() * 1.3, x: r() * 2 - 1 }))
      };
      this.loop = LOOP[this.dataset.art]; this.time = 0;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.reduced.addEventListener('change', () => this.update(), options);
      document.addEventListener('visibilitychange', () => this.update(), options);
      this.resizer = new ResizeObserver(() => this.resize()); this.resizer.observe(this);
      this.watcher = new IntersectionObserver(e => { this.visible = e.some(x => x.isIntersecting); this.update(); }, { rootMargin: '60px 0px' });
      this.watcher.observe(this);
      this.resize();
    }
    resize() {
      const b = this.getBoundingClientRect(); if (!b.width || !b.height) return;
      const ratio = Math.min(devicePixelRatio || 1, 2);
      this.w = b.width; this.h = b.height; this.canvas.width = Math.round(b.width * ratio); this.canvas.height = Math.round(b.height * ratio);
      this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0); this.render();
    }
    render() {
      if (!this.w) return;
      this.ctx.clearRect(0, 0, this.w, this.h);
      if (!this.reduced.matches) this.draw(this.ctx, this.w, this.h, this.time % this.loop, this.env);
    }
    update() {
      cancelAnimationFrame(this.frame); this.last = 0;
      const run = this.visible && !document.hidden && !this.reduced.matches;
      this.dataset.motion = run ? 'running' : 'still';
      if (run) this.frame = requestAnimationFrame(n => this.tick(n)); else this.render();
    }
    tick(now) {
      if (!this.last) this.last = now;
      const dt = (now - this.last) / 1000;
      if (dt >= 1 / FPS - .002) { this.time = (this.time + Math.min(dt, .1)) % this.loop; this.last = now; this.render(); }
      this.frame = requestAnimationFrame(n => this.tick(n));
    }
    disconnectedCallback() { cancelAnimationFrame(this.frame); this.resizer?.disconnect(); this.watcher?.disconnect(); this.controller?.abort(); this.controller = null; }
  });
})();
